import { returnToCreationTab } from '../new/creation-tab-context';
import { withErrorBoundary } from 'app/components/error-boundary';
import { Column } from 'app/design/primitives/box';
import { Button } from 'app/design/primitives/button';
import { ControlledInput } from 'app/design/primitives/input/controlled';
import { Page } from 'app/design/primitives/page';
import { AdministrativeAreaSelect } from 'app/design/primitives/select/administrative-area';
import { CountrySelect } from 'app/design/primitives/select/country';
import { Text } from 'app/design/primitives/text';
import { useForm } from 'app/hooks/form';
import { getI18nLanguage } from 'app/lib/language';
import { getCountryProvincesByLanguage } from 'app/localization/provinces';
import { useAllAddresses, useOneAddress } from 'app/network/address';
import type { NavigationScreen } from 'app/types/navigation';
import { useEffect, useEffectEvent, useRef } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Platform, Pressable, type TextInput } from 'react-native';
import * as yup from 'yup';
import { RemoteError } from 'app/design/primitives/remote-error';
import { type UpdateAddressDTO, useUpdateAddress } from 'app/network/address-mutations';
import type { Listing } from 'app/types/listing';
import { useAlignmentIfDesktop, usePageTitle } from 'app/utils/web-helpers';
import { useOpenNewWithGuard } from 'app/hooks/use-open-new';
import { withDashboardLayout } from 'app/hooks/with-dashboard-layout';
import { withOptionalMetrics } from 'app/utils/performance/with-metrics';
import { useRegisterLoading } from 'app/utils/performance/screen-perf-context';
import { IconX } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import type { Address } from 'app/types/address';
import { navigateToListingWithParams } from 'app/screens/billboard/navigator-helper';

type FormSchema = Pick<UpdateAddressDTO, 'street' | 'unit' | 'city' | 'administrativeArea' | 'postalCode' | 'country'>;

function getAddressFormValues(routeAddress?: Partial<Address> | null, queriedAddress?: Address | null): FormSchema {
  return {
    administrativeArea: routeAddress?.administrativeArea ?? queriedAddress?.administrativeArea,
    city: routeAddress?.city ?? queriedAddress?.city ?? '',
    country: routeAddress?.country ?? queriedAddress?.country ?? undefined,
    postalCode: routeAddress?.postalCode ?? queriedAddress?.postalCode ?? '',
    street: routeAddress?.street ?? queriedAddress?.street ?? '',
    unit: routeAddress?.unit ?? queriedAddress?.unit ?? '',
  };
}

function getResetAddressValues(
  usesProvinces: boolean,
  routeAddress: Partial<Address> | null | undefined,
  address: Address,
) {
  return {
    administrativeArea: usesProvinces ? (routeAddress?.administrativeArea ?? address.administrativeArea) : null,
    city: address.city,
    country: address.country ?? undefined,
    postalCode: address.postalCode,
    street: address.street,
    unit: address.unit,
  };
}

const MyAddressScreenComponent: NavigationScreen<'my-address'> = ({ navigation, route }) => {
  const { t } = useTranslation();
  const { t: tAPI } = useTranslation('api-errors');
  const language = getI18nLanguage();

  const setPageTitle = usePageTitle('my-address');
  useEffect(() => setPageTitle('title'), [setPageTitle]);

  const refUnit = useRef<TextInput>(null);
  const refCity = useRef<TextInput>(null);
  const refPostalCode = useRef<TextInput>(null);

  const schema: yup.ObjectSchema<FormSchema> = yup.object({
    administrativeArea: yup
      .string()
      .min(2)
      .max(3)
      .trim()
      .nullable()
      .defined()
      .when('country', {
        is: (c: string) => getCountryProvincesByLanguage(c, language).length,
        then: (s) => s.required(),
      }),
    unit: yup.string().max(255).trim().nullable().defined(),
    city: yup.string().max(255).trim().nullable().defined(),
    country: yup.string().length(2).trim().required(),
    postalCode: yup
      .string()
      .trim()
      .required(t('postalCodeRequiredError', { ns: 'common' })),
    street: yup.string().max(255).trim().nullable().defined(),
  });

  const myAddressQuery = useOneAddress(route.params.id, { throwOnError: true });
  const allAddressesQuery = useAllAddresses({ enabled: Boolean(route.params.id) });
  const accountCountry = route.params.id
    ? (allAddressesQuery.data?.[0]?.country ?? myAddressQuery.data?.country)
    : undefined;

  const { control, formState, handleSubmit, reset, setValue, watch } = useForm({
    defaultValues: getAddressFormValues(route.params.address, myAddressQuery.data),
    schema,
  });

  const updateAddress = useUpdateAddress({
    onSuccess: (updatedAddress) => {
      reset({
        administrativeArea: updatedAddress.administrativeArea,
        city: updatedAddress.city,
        country: updatedAddress.country ?? undefined,
        postalCode: updatedAddress.postalCode,
        street: updatedAddress.street,
        unit: updatedAddress.unit,
      });
      navigation.goBack();
    },
  });

  const watchedCountry = watch('country');

  useEffect(
    () =>
      setValue(
        'administrativeArea',
        route.params.address?.administrativeArea ?? myAddressQuery.data?.administrativeArea ?? null,
      ),
    [myAddressQuery.data?.administrativeArea, route.params.address?.administrativeArea, setValue, watchedCountry],
  );

  const usesProvinces = (() => {
    return getCountryProvincesByLanguage(watchedCountry, 'en').length > 0;
  })();

  const resetFromAddress = useEffectEvent((address: NonNullable<typeof myAddressQuery.data>) => {
    reset(getResetAddressValues(usesProvinces, route.params.address, address));
  });
  useEffect(() => {
    if (myAddressQuery.data) resetFromAddress(myAddressQuery.data);
  }, [myAddressQuery.data, reset]);

  const openNewWithGuard = useOpenNewWithGuard();
  const onSubmit = handleSubmit((form: FormSchema) => {
    if (route.params.id) {
      updateAddress.mutate({ ...form, id: route.params.id });
    } else if (route.params.goBackTo === 'publish') {
      returnToCreationTab(navigation, { newAddress: form as Listing['address'] });
    } else if (route.params.goBackTo === 'new') {
      openNewWithGuard(navigation, { newAddress: form as Listing['address'] });
    } else if (route.params.goBackTo === 'one-listing') {
      navigateToListingWithParams(navigation, {
        id: route.params.listingId!,
        newAddress: form as Listing['address'],
      });
    } else if (route.params.goBackTo === 'edit-listing') {
      navigation.navigate(
        'edit-listing',
        {
          id: route.params.listingId!,
          newAddress: form as Listing['address'],
        },
        { pop: true },
      );
    }
  });

  useRegisterLoading(myAddressQuery.isPending, 'my-address-query');

  const aligment = useAlignmentIfDesktop();

  return (
    <Page>
      {!route.params.id ? (
        <Pressable
          onPress={navigation.goBack}
          style={{ position: 'absolute', top: Platform.OS === 'ios' ? -30 : 0, left: 0, zIndex: 10 }}
        >
          <IconX strokeWidth={ICON_STROKE_WIDTH} />
        </Pressable>
      ) : null}
      <Text fontWeight="600" marginBottom="spacing.general.x8" textAlign={aligment} variant="headerXSmall">
        {t('title', { ns: 'my-address' })}
      </Text>

      <Column gap="spacing.general.x4">
        <ControlledInput
          {...{ control }}
          autoComplete="street-address"
          label={t('streetFieldLabel', { ns: 'my-address' })}
          name="street"
          onSubmitEditing={() => refUnit.current?.focus()}
          returnKeyType="next"
          submitBehavior="submit"
          textContentType="streetAddressLine1"
        />

        <ControlledInput
          {...{ control }}
          innerRef={refUnit}
          label={t('unitFieldLabel', { ns: 'my-address' })}
          name="unit"
          nullable
          onSubmitEditing={() => refCity.current?.focus()}
          returnKeyType="next"
          submitBehavior="submit"
          textContentType="streetAddressLine2"
        />

        <ControlledInput
          {...{ control }}
          autoComplete="postal-address-locality"
          innerRef={refCity}
          label={t('cityFieldLabel', { ns: 'my-address' })}
          name="city"
          onSubmitEditing={() => refPostalCode.current?.focus()}
          returnKeyType="next"
          submitBehavior="submit"
          textContentType="addressCity"
        />

        <ControlledInput
          {...{ control }}
          autoComplete="postal-code"
          innerRef={refPostalCode}
          label={t('postalCodeFieldLabel', { ns: 'my-address' })}
          name="postalCode"
          textContentType="postalCode"
        />

        <Controller
          {...{ control }}
          name="country"
          render={({ field, fieldState }) => (
            <CountrySelect
              accountCountry={accountCountry}
              error={fieldState.error}
              label={t('countryFieldLabel', { ns: 'my-address' })}
              onChange={field.onChange}
              value={field.value}
            />
          )}
        />

        {accountCountry ? (
          <Text color="text.tertiary" variant="bodySmall">
            {t('countryRegionLockedHelp', { ns: 'my-address' })}
          </Text>
        ) : null}
        {usesProvinces ? (
          <Controller
            {...{ control }}
            name="administrativeArea"
            render={({ field, fieldState }) => (
              <AdministrativeAreaSelect
                country={watchedCountry!}
                disabled={!watchedCountry}
                error={fieldState.error}
                label={t('administrativeAreaFieldLabel', { ns: 'my-address' })}
                onChange={field.onChange}
                value={field.value}
              />
            )}
          />
        ) : null}

        {updateAddress.error ? (
          <RemoteError {...{ error: updateAddress.error, message: tAPI(updateAddress.error.message) }} />
        ) : null}

        <Button
          disabled={!formState.isDirty || !formState.isValid}
          loading={updateAddress.isPending}
          onPress={() => void onSubmit()}
          variant="primary"
        >
          {t('primaryButtonText', { ns: 'my-address' })}
        </Button>
      </Column>
    </Page>
  );
};

export const MyAddressScreen: NavigationScreen<'my-address'> = withOptionalMetrics(
  'MyAddressScreen',
  withErrorBoundary(withDashboardLayout(MyAddressScreenComponent)),
);
